"use client";

import { useEffect } from "react";
import { registerServiceWorker, SW_SEM_FETCH } from "@/lib/push";

/**
 * Tiny client component that registers the Service Worker on mount.
 * Included in the root layout so it runs on every page.
 */
export function SWRegister() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    // Em desenvolvimento o worker regista-se SEM `fetch`; numa frame do Tempo
    // não se regista de todo.
    //
    // O `sw.js` com `fetch` chama `respondWith()` em todos os GET da origem e
    // faz `clients.claim()` ao activar, portanto assume o controlo de cada
    // frame novo que o Tempo cria para uma route storyboard. O pool do Tempo dá
    // 5s para o frame ficar de pé; com o worker a intermediar a navegação e as
    // centenas de pedidos de chunks do `next dev`, o orçamento estoura e a
    // storyboard morre com «acquire frame failed: create timed out after
    // 5000ms».
    //
    // O que NÃO se pode fazer é o que aqui estava: não registar nada em
    // desenvolvimento. Isso apagava as notificações do separador normal da app
    // — a de `localhost` é uma origem segura e tem push como qualquer outra —,
    // e quem carregasse em «Activar» esperava dez segundos por um
    // `serviceWorker.ready` que nunca ia ficar pronto, para receber «o
    // navegador não conseguiu preparar as notificações».
    //
    // A separação está no `semfetch=1`: um worker sem listener de `fetch` é
    // saltado pelo browser na navegação e não custa nada a uma frame, mas
    // recebe push na mesma — a subscrição é do registo, não do listener.
    //
    // `__TEMPO__` é injectado em runtime pelo canvas, portanto só existe mesmo
    // dentro de um frame do Tempo e dispensa rede; `window.top !== window.self`
    // cobre a frame que ainda não recebeu a injecção. Já o `NEXT_PUBLIC_TEMPO`
    // é inlined no build e fica visível no cliente: leva a mesma guarda de
    // `NODE_ENV` que o `api-auth.ts` lhe põe. Um build de produção feito numa
    // máquina com a variável ligada aleijaria o worker de toda a gente.
    const emDesenvolvimento = Boolean(
      process.env.NEXT_PUBLIC_TEMPO && process.env.NODE_ENV !== "production",
    );
    const numaFrame = Boolean(window.__TEMPO__) || window.top !== window.self;

    if (numaFrame) {
      // Limpa só os workers que interceptam pedidos — os legados, e os de um
      // build de produção visto antes nesta origem. Desregistar também o
      // `semfetch=1` seria cancelar, sem aviso, a subscrição push que a pessoa
      // acabou de activar no separador da app: basta abrir uma storyboard.
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(
            registrations
              .filter((r) => {
                const worker = r.active ?? r.waiting ?? r.installing;
                return !worker?.scriptURL.includes(SW_SEM_FETCH);
              })
              .map((r) => r.unregister()),
          ),
        )
        .catch(() => {
          /* Não havia nada registado para desfazer. */
        });
      return;
    }

    registerServiceWorker({ semFetch: emDesenvolvimento });
  }, []);

  return null;
}
